import { BlogArticle, BlogArticleAdapter } from '@shared/models/blog-article.model';
import { BlogPage, BlogPageAdapter } from '@shared/models/blog-page.model';
import { BlogService, TrackingService } from '@core/services';
import { Component, Inject, OnInit, DOCUMENT } from '@angular/core';
import { environment } from '@env/environment';
import { take } from 'rxjs/operators';
import { GhGlobal } from '@shared/helpers/global-functions';
import { ISocialShare } from '@core/seo/social-share/social-share.interface';
import { Location } from '@angular/common';
import { JsonLdService } from '@core/seo/json-ld/json-ld.service';
import { SocialShareService } from '@core/seo/social-share/social-share.service';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
    selector: 'gh-home',
    templateUrl: 'home.component.html',
    standalone: false
})

export class HomeComponent implements OnInit {
  cdnPath = environment.cdnPath;
  articlesFeatured: BlogArticle[];
  articlesOdd: BlogArticle[] = [];
  articlesEven: BlogArticle[] = [];
  articlesOddHeight: number;
  articlesEvenHeight: number;
  articlesSequence: string[] = ["odd", "even", "even", "odd", "even", "odd", "even"];
  articleCount: number;
  sequenceCount: number = 0;
  pager: BlogPage;
  altPager: BlogPage;
  signup = environment.signup;
  showSocialCampaign = environment.socialCampaign;
  currentPage: number = 1;
  pageIndex: number = 0;
  lastPage: number;
  apiPage: number;
  pagePreviewSize: number = 5;
  pagePreviewList: Array<number> = [];
  categories = [];
  totalPages: number;
  altTotalPages: number;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private jsonLdService: JsonLdService,
    private socialShareService: SocialShareService,
    private blogArticleAdapter: BlogArticleAdapter,
    private blogPageAdapter: BlogPageAdapter,
    private blogService: BlogService,
    private trackingService: TrackingService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    protected location: Location
  ) { }

  ngOnInit() {
    GhGlobal.injectStyle('', this.document);

    //Check for query params
    this.activatedRoute.params.subscribe((params: Params): void => {
      this.currentPage = +params.page ? +params.page : 1;
      this.pageIndex = this.currentPage - 1;
      this.getArticlesPaged(this.pageIndex);

    });

    this.getFeaturedArticles();
    this.getCategories();
    this.getAltTotalPages()
  }

  gotoPage(ev: any) {
    this.getArticlesPaged(ev.pageIndex);
  }

  private getFeaturedArticles() {
    this.blogService.getArticlesFeatured()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (res && !res.error) {
          const articles: BlogArticle[] = res || [];
          this.articlesFeatured = [];
          articles.forEach((article) => {
            const art = this.blogArticleAdapter.adapt(article);
            this.articlesFeatured.push(art);
          });
        }
      });
  }

  private getArticlesPaged(pageNumber = 0) {
    this.blogService.getMainPagedArticles(pageNumber)
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res || res.error) {
          return;
        }

        this.pager = this.blogPageAdapter.adapt(res);
        this.totalPages = this.pager.totalPages;
        if (this.currentPage > this.totalPages) {
          this.notFound();
          return;
        }

        this.articleCount = this.articlesEven.length + this.articlesOdd.length;

        (res.content || []).forEach((article, _) => {
          const adaptedArticle = this.blogArticleAdapter.adapt(article);

          this.articlesOddHeight = 622 * this.articlesOdd.length;
          this.articlesEvenHeight = 428 * this.articlesEven.length;

          if (this.articlesOddHeight <= this.articlesEvenHeight) {
            this.articlesOdd.push(adaptedArticle);
          } else {
            this.articlesEven.push(adaptedArticle);
          }
        });
      });


  }

  public getCategories() {
    this.blogService.getCategories()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (res && !res.error) {
          this.categories.push(...res);
          this.categories = this.categories.sort((p1: any, p2: any) => GhGlobal.compare(p1.position, p2.position, true));
        }
      });
  }

  private getAltTotalPages() {
    const locale = environment.locale === 'en-us' ? 'en-gb' : 'en-us';

    this.blogService.getMainPagedArticles(0, locale)
      .pipe(take(1))
      .subscribe((res) => {
        if (res && !res.error) {
          this.altPager = this.blogPageAdapter.adapt(res);
          this.altTotalPages = this.altPager.totalPages;
        }
        this.setSeoData(this.articlesFeatured[0]);
      });
  }

  private notFound() {
    this.router.navigate(['/404']);
  }

  private setSeoData(article: BlogArticle) {
    if (!article) {
      return;
    }

    const tags = article.tags ? article.tags.split(' ').join() : '';
    const seoData: ISocialShare = {
      title: `${environment.homeMetaTitle} | GoHenry`,
      keywords: `gohenry,${tags},blog`,
      description: article.description,
      image: article.heroImageUrl,
      url: environment.location + this.location.path(),
      type: `article.${article.title}`,
      author: article.author.fullName,
      section: article.category.name,
      published: article.publishDate,
      modified: article.modified,
      hashes: article.hashes,
      totalPages: [this.currentPage, this.totalPages, this.altTotalPages]
    };

    const jsonLdObject = this.jsonLdService.getObject('Website', {
      name: 'GoHenry | Making every kid good with money – blog',
      url: seoData.url,
      description: 'The GoHenry pocket money card is a smart solution for managing your child\'s money and giving money to children, through an allowance prepaid card.'
    });

    const jsonLdObject2 = this.jsonLdService.getObject('Organization', {
      url: environment.website,
      logo: environment.jsonImage,
      legalName: 'GoHenry',
      name: 'GoHenry'
    });

    this.socialShareService.setData(seoData);
    this.jsonLdService.setData([jsonLdObject, jsonLdObject2]);
    this.trackingService.setDataLayerAttributes({
      event: 'pageView',
      event_name: 'page_view',
      page: {
        location: '/',
        environment: environment.env,
        locale: environment.locale,
        title: seoData.title,
        feature: 'blog',
      }
    });
  }

}